import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useCart } from '../../hooks/useCart';
import { useWishlist } from '../../hooks/useWishlist';
import { useSEO } from '../../hooks/useSEO';
import ImageGallery from './components/ImageGallery';
import VariantSelector from './components/VariantSelector';
import CartDrawer from '../home/components/CartDrawer';

interface ProductImage {
  url: string;
  altText: string;
  width: number;
  height: number;
}

interface ProductOption {
  id: string;
  name: string;
  values: string[];
}

interface ProductVariant {
  id: string;
  title: string;
  availableForSale: boolean;
  quantityAvailable: number;
  price: number;
  currencyCode: string;
  compareAtPrice: number | null;
  selectedOptions: { name: string; value: string }[];
  image: { url: string; altText: string } | null;
}

interface Product {
  id: string;
  title: string;
  descriptionHtml: string;
  productType: string;
  tags: string[];
  vendor: string;
  images: ProductImage[];
  options: ProductOption[];
  variants: ProductVariant[];
  priceRange: { min: number; max: number; currencyCode: string };
}

const SUPABASE_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;
const SITE_URL = import.meta.env.VITE_SITE_URL || '';

export default function ProductDetailPage() {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const { addItem, openCart } = useCart();
  const { toggleItem, isWishlisted } = useWishlist();

  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedOptions, setSelectedOptions] = useState<Record<string, string>>({});
  const [quantity, setQuantity] = useState(1);
  const [added, setAdded] = useState(false);
  const [navScrolled, setNavScrolled] = useState(false);

  // Strip HTML tags from description
  const stripHtml = (html: string): string => {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };

  // Compute SEO values at the top level
  const selectedVariantForSEO = product?.variants.find((v) =>
    v.selectedOptions.every((so) => selectedOptions[so.name] === so.value)
  ) ?? product?.variants[0] ?? null;

  const plainDescription = product ? stripHtml(product.descriptionHtml) : '';
  const metaDescription = plainDescription.length > 160
    ? plainDescription.substring(0, 157) + '...'
    : plainDescription || `${product?.title ?? ''} - Shop luxury streetwear at Motion Boyz`;

  const seoPrice = selectedVariantForSEO?.price ?? product?.priceRange.min ?? 0;
  const seoCurrencyCode = selectedVariantForSEO?.currencyCode ?? product?.priceRange.currencyCode ?? 'USD';
  const seoIsAvailable = selectedVariantForSEO?.availableForSale ?? false;

  useSEO({
    title: product ? `${product.title} | Motion Boyz` : 'Motion Boyz',
    description: metaDescription,
    canonical: productId ? `/product/${productId}` : '/',
    jsonLd: product ? [
      {
        '@type': 'Product',
        name: product.title,
        description: plainDescription,
        image: product.images.length > 0 ? product.images[0].url : undefined,
        brand: {
          '@type': 'Brand',
          name: 'Motion Boyz',
        },
        sku: productId,
        offers: {
          '@type': 'Offer',
          url: `${SITE_URL}/product/${productId}`,
          priceCurrency: seoCurrencyCode,
          price: seoPrice.toFixed(2),
          availability: seoIsAvailable
            ? 'https://schema.org/InStock'
            : 'https://schema.org/OutOfStock',
          seller: {
            '@type': 'Organization',
            name: 'Motion Boyz',
          },
        },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL || 'https://example.com' },
          { '@type': 'ListItem', position: 2, name: 'Shop', item: `${SITE_URL || 'https://example.com'}/shop` },
          { '@type': 'ListItem', position: 3, name: product.title, item: `${SITE_URL || 'https://example.com'}/product/${productId}` },
        ],
      },
    ] : undefined,
  });

  useEffect(() => {
    const handleScroll = () => setNavScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (!productId) return;
    setLoading(true);
    setError(null);

    fetch(
      `${SUPABASE_URL}/functions/v1/shopify-product-detail?productId=${productId}`,
      { headers: { apikey: SUPABASE_ANON_KEY } }
    )
      .then((r) => r.json())
      .then((data) => {
        if (data.error) throw new Error(String(data.error));
        setProduct(data.product);
        // Pre-select first available option values
        const defaults: Record<string, string> = {};
        data.product.options.forEach((opt: ProductOption) => {
          defaults[opt.name] = opt.values[0];
        });
        setSelectedOptions(defaults);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [productId]);

  // Find the matching variant for current selections
  const selectedVariant = product?.variants.find((v) =>
    v.selectedOptions.every((so) => selectedOptions[so.name] === so.value)
  ) ?? product?.variants[0] ?? null;

  // Build set of unavailable option combos
  const unavailableOptions = useCallback((): Set<string> => {
    if (!product) return new Set();
    const unavailable = new Set<string>();
    product.options.forEach((opt) => {
      opt.values.forEach((val) => {
        const testOptions = { ...selectedOptions, [opt.name]: val };
        const match = product.variants.find((v) =>
          v.selectedOptions.every((so) => testOptions[so.name] === so.value)
        );
        if (match && !match.availableForSale) {
          unavailable.add(`${opt.name}:${val}`);
        }
      });
    });
    return unavailable;
  }, [product, selectedOptions])();

  const handleOptionSelect = (optionName: string, value: string) => {
    setSelectedOptions((prev) => ({ ...prev, [optionName]: value }));
  };

  const handleAddToCart = () => {
    if (!product || !selectedVariant || !selectedVariant.availableForSale) return;
    for (let i = 0; i < quantity; i++) {
      addItem({
        id: product.id,
        variantId: selectedVariant.id,
        name: product.title,
        price: selectedVariant.price,
        image: product.images[0]?.url ?? '',
      });
    }
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
    openCart();
  };

  const handleToggleWishlist = () => {
    if (!product) return;
    toggleItem({
      id: product.id,
      name: product.title,
      price: displayPrice,
      currencyCode,
      image: product.images[0]?.url ?? '',
      category: product.productType ?? '',
      availableForSale: isAvailable,
      variantId: selectedVariant?.id ?? null,
    });
  };

  const displayPrice = selectedVariant?.price ?? product?.priceRange.min ?? 0;
  const comparePrice = selectedVariant?.compareAtPrice ?? null;
  const currencyCode = selectedVariant?.currencyCode ?? product?.priceRange.currencyCode ?? 'USD';
  const isAvailable = selectedVariant?.availableForSale ?? false;
  const maxQty = selectedVariant?.quantityAvailable ?? 99;

  return (
    <div className="bg-black min-h-screen text-white">
      <CartDrawer />

      {/* Top Nav */}
      <nav
        className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
          navScrolled
            ? 'bg-black/90 backdrop-blur-md border-b border-[#00d4ff]/10 shadow-[0_4px_30px_rgba(0,212,255,0.08)]'
            : 'bg-black/60 backdrop-blur-sm'
        }`}
      >
        <div className="w-[90%] max-w-[1400px] mx-auto flex items-center justify-between h-16">
          <Link
            to="/"
            className="flex items-center gap-2 cursor-pointer whitespace-nowrap group"
          >
            <img
              src="https://static.readdy.ai/image/7d0ce02f18177c7f8cb3537c80e36c26/38e85e5700c485e1f17cbbb7a365341f.png"
              alt="Motion Boyz"
              className="h-9 w-auto object-contain"
            />
            <span
              className="text-white font-bold text-lg tracking-widest uppercase group-hover:text-[#00d4ff] transition-colors duration-300"
              style={{ textShadow: '0 0 12px rgba(0,212,255,0.5)' }}
            >
              Motion Boyz
            </span>
          </Link>

          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-400 hover:text-[#00d4ff] text-sm tracking-widest uppercase transition-colors duration-300 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-left-line text-base"></i>
            Back
          </button>
        </div>
      </nav>

      {/* Breadcrumb */}
      <div className="w-[90%] max-w-[1400px] mx-auto pt-28 pb-4">
        <div className="flex items-center gap-2 text-xs text-gray-500 tracking-widest uppercase">
          <Link to="/" className="hover:text-[#00d4ff] transition-colors duration-300 cursor-pointer">Home</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="hover:text-[#00d4ff] transition-colors duration-300 cursor-pointer" onClick={() => navigate(-1)}>Collection</span>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-gray-300">{product?.title ?? 'Product'}</span>
        </div>
      </div>

      {/* Main Content */}
      <main className="w-[90%] max-w-[1400px] mx-auto py-10 pb-32">
        {loading && (
          <div className="flex items-center justify-center h-[60vh]">
            <div className="flex flex-col items-center gap-4">
              <div className="w-14 h-14 border-2 border-[#00d4ff]/30 border-t-[#00d4ff] rounded-full animate-spin"></div>
              <p className="text-gray-500 text-sm tracking-widest uppercase">Loading Product</p>
            </div>
          </div>
        )}

        {error && (
          <div className="flex flex-col items-center justify-center h-[60vh] gap-6">
            <div className="w-16 h-16 flex items-center justify-center rounded-full border border-red-500/30 text-red-400">
              <i className="ri-error-warning-line text-3xl"></i>
            </div>
            <p className="text-gray-400 text-sm">Failed to load product. Please try again.</p>
            <button
              onClick={() => navigate(-1)}
              className="px-6 py-2.5 rounded-full bg-[#00d4ff] text-black text-sm font-bold tracking-widest uppercase cursor-pointer whitespace-nowrap hover:bg-white transition-colors duration-300"
            >
              Go Back
            </button>
          </div>
        )}

        {!loading && !error && product && (
          <div className="grid grid-cols-12 gap-16">
            {/* Left — Image Gallery */}
            <div className="col-span-6">
              <ImageGallery images={product.images} title={product.title} />
            </div>

            {/* Right — Product Info */}
            <div className="col-span-6 flex flex-col gap-8 pt-2">
              {/* Tags */}
              {product.tags.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {product.tags.slice(0, 4).map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 rounded-full text-[10px] tracking-widest uppercase font-semibold border border-[#00d4ff]/30 text-[#00d4ff]"
                      style={{ boxShadow: '0 0 8px rgba(0,212,255,0.15)' }}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}

              {/* Title */}
              <div>
                {product.vendor && (
                  <p className="text-gray-500 text-xs tracking-widest uppercase mb-2">{product.vendor}</p>
                )}
                <h1 className="text-5xl font-bold text-white leading-tight">{product.title}</h1>
              </div>

              {/* Price */}
              <div className="flex items-baseline gap-4">
                <span
                  className="text-4xl font-bold text-[#00d4ff]"
                  style={{ textShadow: '0 0 20px rgba(0,212,255,0.4)' }}
                >
                  {currencyCode === 'USD' ? '$' : currencyCode}{displayPrice.toFixed(2)}
                </span>
                {comparePrice && comparePrice > displayPrice && (
                  <span className="text-xl text-gray-500 line-through">
                    {currencyCode === 'USD' ? '$' : currencyCode}{comparePrice.toFixed(2)}
                  </span>
                )}
                {comparePrice && comparePrice > displayPrice && (
                  <span className="px-2 py-0.5 rounded bg-red-500/20 text-red-400 text-xs font-bold tracking-wide">
                    SALE
                  </span>
                )}
              </div>

              {/* Availability */}
              <div className="flex items-center gap-2">
                <span
                  className={`w-2 h-2 rounded-full ${isAvailable ? 'bg-green-400' : 'bg-red-500'}`}
                  style={isAvailable ? { boxShadow: '0 0 6px rgba(74,222,128,0.8)' } : {}}
                ></span>
                <span className={`text-sm font-medium ${isAvailable ? 'text-green-400' : 'text-red-400'}`}>
                  {isAvailable
                    ? maxQty <= 5
                      ? `Only ${maxQty} left in stock`
                      : 'In Stock'
                    : 'Sold Out'}
                </span>
              </div>

              {/* Divider */}
              <div className="h-px bg-gradient-to-r from-[#00d4ff]/20 via-[#00d4ff]/5 to-transparent"></div>

              {/* Variant Selector */}
              {product.options.length > 0 && !(product.options.length === 1 && product.options[0].values.length === 1) && (
                <VariantSelector
                  options={product.options}
                  selectedOptions={selectedOptions}
                  onSelect={handleOptionSelect}
                  unavailableOptions={unavailableOptions}
                />
              )}

              {/* Quantity */}
              <div>
                <p className="text-gray-400 text-xs tracking-widest uppercase mb-3">Quantity</p>
                <div className="flex items-center gap-0 w-fit border border-gray-700 rounded-full overflow-hidden">
                  <button
                    onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                    className="w-11 h-11 flex items-center justify-center text-white hover:text-[#00d4ff] hover:bg-[#00d4ff]/10 transition-all duration-200 cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-subtract-line"></i>
                  </button>
                  <span className="w-12 text-center text-white font-semibold text-sm">{quantity}</span>
                  <button
                    onClick={() => setQuantity((q) => Math.min(maxQty || 99, q + 1))}
                    className="w-11 h-11 flex items-center justify-center text-white hover:text-[#00d4ff] hover:bg-[#00d4ff]/10 transition-all duration-200 cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-add-line"></i>
                  </button>
                </div>
              </div>

              {/* Add to Cart */}
              <div className="flex gap-4">
                <button
                  onClick={handleAddToCart}
                  disabled={!isAvailable}
                  className={`flex-1 py-4 rounded-full text-sm font-bold tracking-widest uppercase transition-all duration-300 cursor-pointer whitespace-nowrap ${
                    added
                      ? 'bg-green-400 text-black'
                      : isAvailable
                      ? 'bg-[#00d4ff] text-black hover:bg-white hover:scale-[1.02]'
                      : 'bg-gray-800 text-gray-500 cursor-not-allowed'
                  }`}
                  style={isAvailable && !added ? { boxShadow: '0 0 30px rgba(0,212,255,0.4)' } : {}}
                >
                  {added ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-check-line text-base"></i> Added to Cart
                    </span>
                  ) : isAvailable ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-shopping-cart-line text-base"></i> Add to Cart
                    </span>
                  ) : (
                    'Sold Out'
                  )}
                </button>

                {/* Wishlist Button */}
                <button
                  onClick={handleToggleWishlist}
                  title={product && isWishlisted(product.id) ? 'Remove from Wishlist' : 'Save to Wishlist'}
                  className={`w-14 h-14 flex items-center justify-center rounded-full border transition-all duration-300 cursor-pointer whitespace-nowrap flex-shrink-0 ${
                    product && isWishlisted(product.id)
                      ? 'border-red-500 bg-red-500/20 text-red-400 hover:bg-red-500/30'
                      : 'border-gray-700 bg-transparent text-gray-400 hover:border-red-500/60 hover:text-red-400 hover:bg-red-500/10'
                  }`}
                  style={
                    product && isWishlisted(product.id)
                      ? { boxShadow: '0 0 16px rgba(239,68,68,0.3)' }
                      : {}
                  }
                >
                  <i
                    className={`text-xl ${
                      product && isWishlisted(product.id)
                        ? 'ri-heart-fill'
                        : 'ri-heart-line'
                    }`}
                  ></i>
                </button>
              </div>

              {/* Divider */}
              <div className="h-px bg-gradient-to-r from-[#00d4ff]/20 via-[#00d4ff]/5 to-transparent"></div>

              {/* Description */}
              {product.descriptionHtml && (
                <div>
                  <p className="text-gray-400 text-xs tracking-widest uppercase mb-4">Description</p>
                  <div
                    className="text-gray-300 text-sm leading-relaxed product-description"
                    dangerouslySetInnerHTML={{ __html: product.descriptionHtml }}
                  />
                </div>
              )}

              {/* Product Meta */}
              {product.productType && (
                <div className="flex items-center gap-3 text-xs text-gray-600">
                  <i className="ri-price-tag-3-line"></i>
                  <span className="tracking-widest uppercase">{product.productType}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </main>

      <style>{`
        .product-description h1,
        .product-description h2,
        .product-description h3 {
          color: white;
          font-weight: 600;
          margin-bottom: 0.5rem;
        }
        .product-description p {
          margin-bottom: 0.75rem;
          color: #9ca3af;
        }
        .product-description ul,
        .product-description ol {
          padding-left: 1.25rem;
          margin-bottom: 0.75rem;
          color: #9ca3af;
        }
        .product-description li {
          margin-bottom: 0.25rem;
        }
        .product-description a {
          color: #00d4ff;
          text-decoration: underline;
        }
        .product-description strong {
          color: white;
        }
      `}</style>
    </div>
  );
}
